<template>
  <change-password
    v-if="showChange"
    :error="error"
    :loading="loading"
    @cancel-change="cancelChange"
    @submit-change="onUpdatePassword"
  />
  <confirm-auth
    v-else-if="showConfirm"
    :error="error"
    :details="confirmDetails"
    :confirm-state="signInConfirm"
    @cancel-confirm="cancelConfirm"
    @submit-confirm="onConfirmSignin"
  />
  <div
    v-else
    class="auth-screen"
  >
    <loading-spinner v-show="loading" />
    <div class="d-flex align-items-center justify-content-between">
      <h2 class="mb-4">
        Account Access
      </h2>
    </div>
    <b-container class="auth-block has-border">
      <b-row>
        <b-col>
          <label for="user-emailid">Email</label>
          <b-form-input
            id="user-emailid"
            v-model.trim="authData.username"
            size="md"
            type="text"
            @keyup.13="onSignIn"
          />
        </b-col>
        <b-col>
          <label for="user-password">Password</label>
          <button
            type="button"
            class="btn btn-link forgot-pw"
            @click="onForgotPassword"
          >
            I forgot
          </button>
          <b-form-input
            id="user-password"
            v-model.trim="authData.password"
            size="md"
            type="password"
            @keyup.13="onSignIn"
          />
        </b-col>
      </b-row>
      <b-row>
        <b-col class="ml-3 mt-3 d-flex justify-content-end">
          <div
            v-if="!!error"
            class="mr-4 alert alert-danger fade show"
            role="alert"
          >
            {{ error }}
          </div>
          <div class="submit-btn-ct">
            <b-button
              variant="primary"
              title="Sign In"
              class="btn-block"
              @keyup.13="onSignIn"
              @click="onSignIn"
            >
              Sign In
            </b-button>
          </div>
        </b-col>
      </b-row>
    </b-container>
    <div class="mt-4 d-flex">
      <div class="flex-1">
        At CDS Global ensuring online security is a high priority. <br>
        The following measures are designed to ensure your communications
        and transactions on this CDS Global website are as safe and reliable
        as any other means of doing business with us.
      </div>
      <div class="flex-1">
        <ol>
          <li>
            Only individuals authorized by CDS Global may use this system
            and its resources.
          </li>
          <li>Unique IDs and passwords are required for all users.</li>
          <li>
            All users of the system are subject to having their activities
            monitored and reviewed.
          </li>
          <li>
            CDS Global, at its sole discretion, may revoke or terminate a
            user’s access at any time.
          </li>
          <li>
            Should you require assistance, please contact the CDS Global
            Help Desk.
          </li>
        </ol>
      </div>
    </div>
  </div>
</template>

<script>
import { Auth } from 'aws-amplify'
import ConfirmAuth from './ConfirmAuth'
import ChangePassword from './ChangePassword'
import { isValidEmail } from '@/_globals/field-validations'
import LoadingSpinner from '@/components/UIComponents/LoadingSpinner'

export default {
  name: 'SignIn',
  components: {
    ChangePassword,
    ConfirmAuth,
    LoadingSpinner
  },
  data: function () {
    return {
      authData: {},
      authState: 'signIn',
      showConfirm: false,
      showChange: false,
      error: null,
      loading: false,
      user: null,
      emailaddress: '',
      phone: '',
      confirmDetails: {},
      signInConfirm: 'signInConfirm'
    }
  },
  computed: {
    username: function () {
      return this.authData.username || ''
    },
    password: function () {
      return this.authData.password || ''
    }
  },
  methods: {
    onAuthStateChange: function (next) {
      this.$store.dispatch('auth/setAuthState', next)
    },
    async onSignIn() {
      this.error = ''
      this.loading = true
      const invalid = isValidEmail(this.username) !== undefined
      if (!this.password) {
        this.error = 'Password cannot be empty'
        this.loading = false
        return
      }
      if (invalid) {
        this.error = 'Invalid email'
        this.loading = false
      } else {
        try {
          const data = await Auth.signIn(
            this.username,
            this.password
          )
          if (data.signInUserSession === null) {
            console.log('SignInUserSession from amplify auth is null, handle this case')
            if (
              data.challengeName &&
              data.challengeName === 'NEW_PASSWORD_REQUIRED'
            ) {
              this.user = data
              this.loading = false
              this.error = null
              this.showConfirm = false
              this.showChange = true
            } else {
              this.user = data
              this.loading = false
              this.showConfirm = true
              this.showChange = false
            }
          } else {
            // TODO
            // Update Vuejs store to Authenticated status
            this.onAuthStateChange('authenticated', data)
            this.$store.dispatch('auth/setUserToken', data)
            this.$router.push({ name: 'home' })
            this.loading = false
          }
        } catch (err) {
          // console.log(err)
          let error = typeof err === 'string' ? err : err.message
          if (err.code && err.code === 'InvalidParameterException') {
            error = 'Invalid Email or Password'
          }
          this.error = error
          this.loading = false
        }
      }
    },
    async onUpdatePassword(newPassword) {
      try {
        this.error = null
        this.loading = true
        const response = await Auth.completeNewPassword(
          this.user,
          newPassword,
          null
        )
        console.log(
          `completeNewPassword: Response#1 = ${JSON.stringify(
            response,
            null,
            2
          )}`
        )
        this.authData = response
        this.showConfirm = false
        this.loading = false
        const profile = await Auth.currentAuthenticatedUser()
        console.log('Profile of curren user: ', profile)
        this.onAuthStateChange('signIn')
        this.$store.dispatch('auth/setUserToken', profile)
        this.$notify({
          group: 'ConfigTool',
          title: 'Password Updated',
          text: 'Password successfully changed',
          type: 'success'
        })
      } catch (err) {
        const error = typeof err === 'string' ? err : err.message
        this.error = error
        this.loading = false
      }
    },
    async onConfirmSignin(token) {
      this.loading = true
      try {
        await Auth.confirmSignIn(this.user, token, null)
        const profile = await Auth.currentAuthenticatedUser()
        this.onAuthStateChange('authenticated', profile)
        this.$store.dispatch('auth/setUserToken', profile)
      } catch (err) {
        let error = typeof err === 'string' ? err : err.message
        if (!token) {
          error = 'Invalid Token Entered'
        }
        this.error = error
        this.loading = false
      }
    },
    login(username, password) {
      Auth.signIn(username, password)
        .then(user => console.log(user))
        .catch(err => console.log(err))
    },
    signup(username, password, email, phoneNumber) {
      Auth.signUp({
        username,
        password,
        attributes: {
          email, // optional
          phoneNumber // optional - E.164 number convention
          // other custom attributes
        },
        validationData: [] // optional
      })
        .then(data => console.log(data))
        .catch(err => console.log(err))
    },
    cancelConfirm() {
      this.showConfirm = false
    },
    cancelChange() {
      this.showChange = false
    },
    onForgotPassword() {
      this.$store.dispatch('auth/setAuthViewState', 'forgotPassword')
    },
    logout() {
      this.$store.dispatch('clearAll')
    }
  }
}
</script>

<style lang="scss" scoped>
  @import '~@/assets/styles/main.scss';
  @import '~@/assets/styles/auth.scss';
</style>

